import {
  countries,
  isValidCountryCode,
  normalizeAvailableCountries,
  normalizeCountryCodeInputs,
} from './countries';

export type AvailableCountriesMode = 'include' | 'exclude';

export const DEFAULT_AVAILABLE_COUNTRIES_MODE: AvailableCountriesMode =
  'include';
export const ALL_COUNTRIES_KEY = '__ALL_COUNTRIES__';

const RESTRICTED_COUNTRY_CODES = new Set([
  'CU',
  'IR',
  'KP',
  'SY',
  'RU',
  'VE',
  'AF',
  'BY',
  'MM',
  'IQ',
]);

const selectableCountries = countries.filter(
  (country) => !RESTRICTED_COUNTRY_CODES.has(country.code),
);

export const MAX_EXCLUDED_COUNTRY_SELECTIONS = selectableCountries.length - 1;

const countryChoices = selectableCountries.map((country) => ({
  key: country.code,
  label: country.name,
}));

const availableCountryChoices = [
  { key: ALL_COUNTRIES_KEY, label: 'All countries' },
  ...countryChoices,
];

export function getAvailableCountriesChoices(
  mode: AvailableCountriesMode = DEFAULT_AVAILABLE_COUNTRIES_MODE,
) {
  return mode === 'exclude' ? countryChoices : availableCountryChoices;
}

export function getAvailableCountriesSelectValue(
  availableCountries: readonly string[] | undefined,
  mode: AvailableCountriesMode = DEFAULT_AVAILABLE_COUNTRIES_MODE,
): string[] {
  if (mode === 'exclude') {
    return normalizeCountryCodeInputs(availableCountries);
  }

  const normalized = normalizeAvailableCountries(availableCountries);

  return normalized.length === 0 ? [ALL_COUNTRIES_KEY] : normalized;
}

export function resolveAvailableCountriesSelection(
  previousAvailableCountries: readonly string[] | undefined,
  nextSelectedKeys: readonly string[],
  mode: AvailableCountriesMode = DEFAULT_AVAILABLE_COUNTRIES_MODE,
): string[] {
  if (mode === 'exclude') {
    const excludedCountryCodes = normalizeCountryCodeInputs(nextSelectedKeys);

    if (excludedCountryCodes.length >= selectableCountries.length) {
      return normalizeCountryCodeInputs(previousAvailableCountries);
    }

    return excludedCountryCodes;
  }

  const normalizedPrevious = normalizeAvailableCountries(
    previousAvailableCountries,
  );
  const nextSelection = Array.from(
    new Set(nextSelectedKeys.filter((key) => key.length > 0)),
  );
  const hasAllCountries = nextSelection.includes(ALL_COUNTRIES_KEY);
  const nextCountryCodes = normalizeAvailableCountries(
    nextSelection.filter((key) => key !== ALL_COUNTRIES_KEY),
  );

  if (!hasAllCountries) {
    return nextCountryCodes;
  }

  return normalizedPrevious.length === 0 ? nextCountryCodes : [];
}

export function normalizeAvailableCountriesFormValue(value: unknown): string[] {
  return normalizeCountryCodeInputs(
    Array.isArray(value)
      ? value.filter(
          (item): item is string =>
            typeof item === 'string' && item !== ALL_COUNTRIES_KEY,
        )
      : undefined,
  );
}

export function assertValidAvailableCountries(
  availableCountries: readonly string[],
): string[] {
  return availableCountries.filter((code) => !isValidCountryCode(code));
}
